import { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import Tabs from '@/components/Tabs';
import { useAppSelector } from '@/hooks/redux-hooks';
import useDocumentTitleRename from '@/hooks/useDocumentTitleRename';
import { MarketProvider, useMarketContext } from '@/pages/Markets/context/MarketContext';
import { useMarketNavigation } from '@/pages/Markets/hooks/useMarketNavigation';
import { MarketComingSoon } from '@/pages/Markets/MarketComingSoon';
import ShareButton from '@/pages/Markets/MarketsComponents/Share/ShareButton';
import useMarkets from '@/pages/Markets/useMarkets';

import MarketsComponents from './MarketsComponents';
import MarketsSkeleton from './MarketsSkeleton';

const Markets = () => {
  const navigate = useNavigate();
  const params = useParams();
  const status = useAppSelector(state => state.markets.status);
  const markets = useAppSelector(state => state.markets.markets);

  const { activeTab, setActiveTab } = useMarketContext();
  useMarkets();
  useMarketNavigation();

  const fullTitle = useMemo(
    () =>
      ['GX Go', markets?.metadata?.displayName, markets?.pages?.[activeTab]?.name]
        .filter(Boolean)
        .join(' | '),
    [markets, activeTab]
  );

  useDocumentTitleRename(fullTitle);

  if (status === 'loading' || !markets) {
    return <MarketsSkeleton />;
  }

  if (markets.metadata.comingSoon === true) {
    return <MarketComingSoon market={markets} />;
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: 3,
          gap: 1
        }}
      >
        <Typography
          sx={{ mr: 4 }}
          variant='h2'
        >
          {markets?.metadata?.displayName}
        </Typography>

        <Tabs
          activeTab={markets?.pages.length <= activeTab ? 0 : activeTab}
          scrollButtons='auto'
          tabs={markets?.pages.map(({ name }) => ({ name }))}
          onChange={tabId => {
            setActiveTab(tabId);
            navigate(`/markets/${params.marketId}/${markets?.pages[tabId]?.id}`);
          }}
        />
        <Box sx={{ ml: 'auto' }}>
          <ShareButton
            additionalTitle={markets?.pages[activeTab]?.name}
            title={markets?.metadata?.displayName}
          />
        </Box>
      </Box>
      <MarketsComponents components={markets?.pages[activeTab]?.components} />
    </>
  );
};

const WrappedMarkets = () => (
  <MarketProvider>
    <Markets />
  </MarketProvider>
);

export default WrappedMarkets;
